import { useCallback, useState, useEffect } from 'react';

import { Perspective } from '../types';

import { useRealtimeEventHandler } from '@/realtime/useRealtimeEventHandler';
import {
  EventPerspectiveAddedData,
  EventPerspectiveRemovedData,
  EventPerspectiveThumbnailAddedData,
  EventPerspectiveVodReadyData,
  EventPerspectiveEndedData,
  EventDeletedData,
} from '@/realtime/types';
import { enhancedApi } from '@/api/client';

interface UseEventPerspectivesRealtimeParams {
  eventId: string;
  initialPerspectives: Perspective[];
  onEventDeleted?: () => void;
}

/**
 * Hook to handle real-time perspective updates via WebSocket.
 * Subscribes to events:{eventId} topic and updates perspectives list.
 */
export function useEventPerspectivesRealtime({
  eventId,
  initialPerspectives,
  onEventDeleted,
}: UseEventPerspectivesRealtimeParams) {
  const [perspectives, setPerspectives] =
    useState<Perspective[]>(initialPerspectives);

  useEffect(() => {
    setPerspectives(initialPerspectives);
  }, [initialPerspectives]);

  const handlePerspectiveAdded = useCallback(
    async (data: EventPerspectiveAddedData) => {
      const placeholderPerspective: Perspective = {
        id: data.perspective_post_id.toString(),
        username: 'Loading...',
        viewerCount: 0,
        postId: data.perspective_post_id.toString(),
        livestreamId: data.livestream_id || '',
        chatChannelId: '', // Will be populated when full post data is fetched
        streamStatus: 'live',
      };

      setPerspectives(prev => [placeholderPerspective, ...prev]);

      try {
        const post = await enhancedApi.endpoints.getPost.initiate(
          data.perspective_post_id,
        )(
          (result: any) => result,
          () => {},
          {} as any,
        ).unwrap();

        if (post) {
          const fullPerspective: Perspective = {
            id: post.id.toString(),
            username: post.username,
            viewerCount: 0,
            thumbnailUrl: post.photo_url_compressed || undefined,
            postId: post.id.toString(),
            livestreamId: post.livestream_id || '',
            chatChannelId: post.chat_channel_id || '',
            streamStatus: 'live',
            m3u8Url: post.m3u8_url || undefined,
          };

          setPerspectives(prev =>
            prev.map(p =>
              p.postId === data.perspective_post_id.toString()
                ? fullPerspective
                : p,
            ),
          );
        }
      } catch (error) {
        console.error(
          '[EventPerspectives] Failed to fetch perspective:',
          error,
        );
      }
    },
    [],
  );

  const handlePerspectiveRemoved = useCallback(
    (data: EventPerspectiveRemovedData) => {
      setPerspectives(prev =>
        prev.filter(p => p.postId !== data.perspective_post_id.toString()),
      );
    },
    [],
  );

  const handleThumbnailAdded = useCallback(
    (data: EventPerspectiveThumbnailAddedData) => {
      setPerspectives(prev =>
        prev.map(p =>
          p.postId === data.perspective_post_id.toString()
            ? { ...p, thumbnailUrl: data.thumbnail_url }
            : p,
        ),
      );
    },
    [],
  );

  const handleVodReady = useCallback((data: EventPerspectiveVodReadyData) => {
    setPerspectives(prev =>
      prev.map(p =>
        p.postId === data.perspective_post_id.toString()
          ? { ...p, m3u8Url: data.m3u8_url, streamStatus: 'ended' }
          : p,
      ),
    );
  }, []);

  const handlePerspectiveEnded = useCallback(
    (data: EventPerspectiveEndedData) => {
      setPerspectives(prev =>
        prev.map(p =>
          p.postId === data.perspective_post_id.toString()
            ? { ...p, streamStatus: 'ended' }
            : p,
        ),
      );
    },
    [],
  );

  const handleEventDeleted = useCallback(
    (data: EventDeletedData) => {
      onEventDeleted?.();
    },
    [onEventDeleted],
  );

  useRealtimeEventHandler({
    topic: eventId ? `events:${eventId}` : null,
    handlers: {
      'event.perspective_added': event =>
        handlePerspectiveAdded(event.data as EventPerspectiveAddedData),
      'event.perspective_removed': event =>
        handlePerspectiveRemoved(event.data as EventPerspectiveRemovedData),
      'event.perspective_thumbnail_added': event =>
        handleThumbnailAdded(event.data as EventPerspectiveThumbnailAddedData),
      'event.perspective_vod_ready': event =>
        handleVodReady(event.data as EventPerspectiveVodReadyData),
      'event.perspective_ended': event =>
        handlePerspectiveEnded(event.data as EventPerspectiveEndedData),
      'event.deleted': event =>
        handleEventDeleted(event.data as EventDeletedData),
    },
    deps: [
      handlePerspectiveAdded,
      handlePerspectiveRemoved,
      handleThumbnailAdded,
      handleVodReady,
      handlePerspectiveEnded,
      handleEventDeleted,
    ],
  });

  return {
    perspectives,
  };
}
